import { useId, type KeyboardEvent, type ReactNode, type SyntheticEvent } from 'react'
import { Badge } from '@/components/ui/badge'
import { useRoles } from '@/hooks/useRoles'
import type { OperatorRole } from '@/types/api'

interface RoleGateProps {
    roles: OperatorRole[]
    children: ReactNode
    /**
     * When true and the user lacks the required role, render the children with
     * a small "read-only" badge instead of hiding them. Used for surfaces that
     * still make sense to show (eg. an action menu trigger) but must not fire
     * mutations. When false (the default) the children render nothing.
     */
    showReadOnlyAffordance?: boolean
    /**
     * Override the affordance node entirely (eg. a muted "no permission"
     * placeholder that fits the surrounding layout).
     */
    fallback?: ReactNode
}

function swallow(event: SyntheticEvent) {
    event.preventDefault()
    event.stopPropagation()
}

// Tab stays live so keyboard users can move past the affordance; every other key is swallowed so
// no child handler (Enter/Space activation, arrow-key menu openers) can fire.
function swallowUnlessTab(event: KeyboardEvent) {
    if (event.key !== 'Tab') swallow(event)
}

export function RoleGate({ roles, children, showReadOnlyAffordance, fallback }: RoleGateProps) {
    const { hasAnyRole } = useRoles()
    const badgeId = useId()
    if (hasAnyRole(...roles)) return <>{children}</>
    if (fallback !== undefined) return <>{fallback}</>
    if (showReadOnlyAffordance) {
        // The children stay focusable so screen readers can reach them; `aria-disabled` on the
        // group marks every descendant unavailable, and the capture-phase guards stop keyboard and
        // pointer activation before any child handler runs. `pointer-events-none` alone only
        // blocked the mouse (BLO-876).
        return (
            <span
                role="group"
                aria-disabled="true"
                aria-describedby={badgeId}
                className="pointer-events-none inline-flex items-center gap-2 opacity-60"
                onClickCapture={swallow}
                onPointerDownCapture={swallow}
                onMouseDownCapture={swallow}
                onKeyDownCapture={swallowUnlessTab}
                onKeyUpCapture={swallowUnlessTab}
            >
                {children}
                <Badge id={badgeId} variant="muted">
                    read-only
                </Badge>
            </span>
        )
    }
    return null
}
